import get from 'lodash/get';
import set from 'lodash/set';
import produce from 'immer';
import {
  IEmployerAddress,
  IEmployerContact,
  IEmployerCourse,
} from '../employer/employer-reducer';
import { ImportLearnerFeatureStatus } from '../enums/global-enums';
import { ILearnerCentre } from '../../utils/exportLearnerHelper';
import { IOption } from '../../components/atoms/form-fields/select/Select';

export interface ILearnerContact {
  emailAddress: string;
  homePhoneNumber: string;
  mobilePhoneNumber: string;
}

export interface ILLDDOptions {
  llddCode: number;
  llddPrimaryCondition: number;
  llddAdditionalConditions: number[];
}

export interface ILLDDProps {
  label: string;
  description: string;
  value: ILLDDOptions;
  oldValue?: ILLDDOptions;
  newValue?: ILLDDOptions;
  options?: {
    llddStatusOptions: IOption[];
    llddConditionOptions: IOption[];
  };
}

export interface ILearnerAddress {
  building: string;
  street: string;
  city: string;
  county: string;
  postcode: string;
}

export interface ILearnerEmployer {
  name: string;
  sector: string;
  levy: boolean;
  edsNumber: string;
  address?: IEmployerAddress;
  course?: IEmployerCourse;
  contact?: IEmployerContact;
}

export interface ILearner {
  admins: string;
  adminIds: string;
  completedStatus: number;
  enrolmentStatus: number | 1;
  id?: string;
  firstName: string;
  surname: string;
  lastModified?: string;
  createdOn?: string;
  nationalInsuranceNumber?: string;
  uln?: string;
  dateOfBirth: string;
  contact?: ILearnerContact;
  address?: ILearnerAddress;
  employer?: ILearnerEmployer;
  employerId?: string;
  employerName?: string;
  sectorId?: string;
  isCompleted?: boolean;
  ePortfolioId?: number;
  ilrId?: number;
  llddCode: number;
  llddPrimaryCondition: number;
  llddAdditionalConditions: number[];
  ethnicity?: string;
  ethnicityCode?: number;
  sex?: string;
  sexCode?: string;
  tutor?: string;
  tutorId?: number;
  centre: ILearnerCentre;
  centreId?: number;
  centreCount?: number;
  centreName?: string;
  programme?: programme;
  programmeDuration?: number;
  contractualHours?: number;
  statutoryLeave?: number;
  minimumOtj?: number;
  plannedOtj?: number;
  maximumFunding?: number;
  totalNegotiatedPrice?: number;
  archived?: boolean;
  archivedDate?: string;
}

export interface programme {
  programmeStartDate?: string;
  programmeEndDate?: string;
  practicalPeriodStartDate?: string;
  practicalPeriodEndDate?: string;
  programmeDuration?: string;
  contractualHours?: number;
  minimumOtj?: number;
  plannedOtj?: string;
  statutoryLeave?: string;
  totalNegotiatedPrice?: string;
  maximumFunding?: string;
  isRplData?: boolean;
}

export interface ILearnerInList {
  id: string;
  email: string;
  courseName: string | null;
  employerName: string | null;
  employerId: string | null;
  learnerId: string;
  name: string;
  lastModified: string;
  completedStatus: number;
  enrolmentStatus: number | 0;
  dateOfBirth: string;
  uln: string;
  archived: boolean;
  archivedDate: string;
}

export interface ILearnerContextState {
  currentLearner: ILearner | null;
  learners: ILearnerInList[];
}

export interface ISetLearnerAction {
  type: 'SET_LEARNER';
  payload: ILearner | null;
}

export interface ILearnerImport {
  importLearners: ImportLearnerFeatureStatus | null;
}

// object comes down from API incomplete
// this ensures that the structure is complete with all empty fields as strings
// allows us to have cleaner type checking in rest of codebase
const addMissingFieldsToLearner = (learner: ILearner) => {
  return produce(learner, (updated) => {
    [
      'uln',
      'nationalInsuranceNumber',
      'contact.homePhoneNumber',
      'contact.mobilePhoneNumber',
      'contact.emailAddress',
      'address.building',
      'address.street',
      'address.county',
      'address.city',
      'address.postcode',
    ].forEach((key) => {
      if (!get(updated, key)) {
        set(updated, key, '');
      }
    });
    return updated;
  });
};

export type ILearnerContextActions = ISetLearnerAction;

export const initialLearnerState: ILearnerContextState = {
  currentLearner: null,
  learners: [],
};

export interface ILearnerLLDDCondition {
  id: number;
  code: number;
  definition: string;
}

export interface ILearnerEthnicity {
  Id: number;
  code: number;
  definition: string;
}

export interface ILearnerSex {
  Id: number;
  code: number;
  definition: string;
}

export interface ILearnerTutor {
  id: number;
  definition: string;
}

export interface IEnrolmentStatus {
  id: number;
  description: string;
}

export enum CompleteStatus {
  Incomplete = 0,
  Completed = 3,
}

export interface ILearnerAdmin {
  id?: string;
  adminIdList: string;
}

export interface ILearnerActivityTableItem {
  id: number;
  createdOn: string;
  activityCategory: string;
  activityAction: string;
  activityMessage: string;
  userId: string;
  username: string;
  userRole: string;
}

export const learnerReducer = (
  state: ILearnerContextState,
  action: ILearnerContextActions,
) => {
  switch (action.type) {
    case 'SET_LEARNER':
      return {
        ...state,
        currentLearner:
          action.payload !== null
            ? addMissingFieldsToLearner(action.payload)
            : null,
      };
    default:
      return state;
  }
};
